import { API } from '../config/config';

const CACHE_PREFIX = 'paymenttypes:v1:';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;
const PAYMENT_TYPES_UPDATED_EVENT = 'paymenttypes:updated';
const memoryCache = new Map();

const hasWindow = () => typeof window !== 'undefined';
const hasLocalStorage = () => hasWindow() && !!window.localStorage;

const cloneData = (data) => {
  if (typeof structuredClone === 'function') {
    return structuredClone(data);
  }
  return JSON.parse(JSON.stringify(data));
};

const cacheKey = (storeId) => `${CACHE_PREFIX}${storeId}`;

const readStorageCache = (storeId) => {
  if (!hasLocalStorage()) return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(storeId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.data)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeStorageCache = (storeId, entry) => {
  if (!hasLocalStorage()) return;
  try {
    window.localStorage.setItem(cacheKey(storeId), JSON.stringify(entry));
  } catch {
    // ignore storage errors
  }
};

const removeStorageCache = (storeId) => {
  if (!hasLocalStorage()) return;
  try {
    window.localStorage.removeItem(cacheKey(storeId));
  } catch {
    // ignore storage errors
  }
};

const isFresh = (entry, maxAgeMs = CACHE_TTL_MS) => {
  if (!entry || !Array.isArray(entry.data)) return false;
  if (!entry.updatedAt) return false;
  return Date.now() - entry.updatedAt <= maxAgeMs;
};

const getCachedData = (storeId, maxAgeMs = CACHE_TTL_MS) => {
  const inMemory = memoryCache.get(storeId);
  if (isFresh(inMemory, maxAgeMs)) {
    return cloneData(inMemory.data);
  }

  const inStorage = readStorageCache(storeId);
  if (isFresh(inStorage, maxAgeMs)) {
    memoryCache.set(storeId, inStorage);
    return cloneData(inStorage.data);
  }

  return null;
};

const setCachedData = (storeId, data) => {
  const safeData = Array.isArray(data) ? data : [];
  const entry = {
    data: safeData,
    updatedAt: Date.now(),
  };

  memoryCache.set(storeId, entry);
  writeStorageCache(storeId, entry);
};

const clearCache = (storeId) => {
  memoryCache.delete(storeId);
  removeStorageCache(storeId);
};

const emitPaymentTypesUpdated = (storeId) => {
  if (!hasWindow()) return;
  window.dispatchEvent(new CustomEvent(PAYMENT_TYPES_UPDATED_EVENT, {
    detail: { storeId, updatedAt: Date.now() },
  }));
};

const normalizeListArgs = (signalOrOptions, maybeOptions) => {
  const isAbortSignal = (value) => typeof AbortSignal !== 'undefined' && value instanceof AbortSignal;

  if (isAbortSignal(signalOrOptions)) {
    return {
      signal: signalOrOptions,
      ...(maybeOptions || {}),
    };
  }

  return signalOrOptions || {};
};

const PaymentTypesService = {
  listByStore: (storeId, signalOrOptions, maybeOptions) => getAll(storeId, signalOrOptions, maybeOptions),
  getById: (storeId, id) => getById(storeId, id),
  create: (storeId, data) => create(storeId, data),
  update: (storeId, id, data) => update(storeId, id, data),
  delete: (storeId, id) => remove(storeId, id),
  invalidateStoreCache: (storeId) => {
    clearCache(storeId);
    emitPaymentTypesUpdated(storeId);
  },
  subscribeToChanges: (callback) => {
    if (!hasWindow()) return () => {};

    const customEventHandler = (event) => callback?.(event?.detail || {});
    const storageHandler = (event) => {
      if (!event?.key || !event.key.startsWith(CACHE_PREFIX)) return;
      const storeId = event.key.replace(CACHE_PREFIX, '');
      callback?.({ storeId, updatedAt: Date.now() });
    };

    window.addEventListener(PAYMENT_TYPES_UPDATED_EVENT, customEventHandler);
    window.addEventListener('storage', storageHandler);

    return () => {
      window.removeEventListener(PAYMENT_TYPES_UPDATED_EVENT, customEventHandler);
      window.removeEventListener('storage', storageHandler);
    };
  },
};

const base = (storeId) => `/stores/${storeId}/paymenttypes`;

const getAll = async (storeId, signalOrOptions, maybeOptions) => {
  const options = normalizeListArgs(signalOrOptions, maybeOptions);
  const {
    signal,
    forceRefresh = false,
    maxAgeMs = CACHE_TTL_MS,
  } = options;

  if (!forceRefresh) {
    const cached = getCachedData(storeId, maxAgeMs);
    if (cached) return cached;
  }

  try {
    const response = await API.get(base(storeId), { signal });
    const data = Array.isArray(response.data) ? response.data : [];
    setCachedData(storeId, data);
    return cloneData(data);
  } catch (err) {
    console.error('Error fetching payment types:', err);
    throw err;
  }
};

const getById = async (storeId, id) => {
  try {
    const response = await API.get(`${base(storeId)}/${id}`);
    return response.data;
  } catch (err) {
    console.error('Error fetching payment type:', err);
    throw new Error(err);
  }
};

const create = async (storeId, data) => {
  try {
    const cleanData = { ...data };
    delete cleanData._id;
    delete cleanData.id;
    delete cleanData.created_at;
    delete cleanData.updated_at;
    const response = await API.post(base(storeId), cleanData);
    clearCache(storeId);
    emitPaymentTypesUpdated(storeId);
    return response.data;
  } catch (err) {
    console.error('Error creating payment type:', err);
    throw err;
  }
};

const update = async (storeId, id, data) => {
  try {
    const response = await API.put(`${base(storeId)}/${id}`, data);
    clearCache(storeId);
    emitPaymentTypesUpdated(storeId);
    return response.data;
  } catch (err) {
    console.error('Error updating payment type:', err);
    throw err;
  }
};

const remove = async (storeId, id) => {
  try {
    const response = await API.delete(`${base(storeId)}/${id}`);
    clearCache(storeId);
    emitPaymentTypesUpdated(storeId);
    return response.data;
  } catch (err) {
    console.error('Error deleting payment type:', err);
    throw err;
  }
};

export default PaymentTypesService;
